import { forwardRef } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Icon } from '../../atoms/icon'

const marketingBannerVariants = tv({
  slots: {
    root: 'font-body relative flex min-h-14 w-full items-center justify-center border-b px-16 py-4',
    content: 'text-body-lg flex items-center gap-2 text-center font-semibold',
    text: '',
    dismiss:
      'text-body-lg focus-visible:ring-primary-200 absolute right-6 top-1/2 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
  },
  variants: {
    variant: {
      info: {
        root: 'bg-information-bg border-information-stroke',
        content: 'text-information-title',
        dismiss: 'text-information-icon'
      },
      success: {
        root: 'bg-success-bg border-success-stroke',
        content: 'text-success-title',
        dismiss: 'text-success-icon'
      },
      warning: {
        root: 'bg-warning-bg border-warning-stroke',
        content: 'text-warning-title',
        dismiss: 'text-warning-icon'
      },
      error: {
        root: 'bg-danger-bg border-danger-stroke',
        content: 'text-danger-title',
        dismiss: 'text-danger-icon'
      }
    },
    gradient: {
      true: {}
    },
    interactive: {
      true: {
        // !no-underline: the apps' RootLayout applies `& a { text-decoration:
        // underline }` globally via emotion; its `.css-x a` selector
        // outspecifies the bare utility, underlining the whole message and the
        // arrow icon. Hover keeps underlining only the text slot below.
        content:
          'focus-visible:ring-primary-200 group cursor-pointer rounded-sm !no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        text: 'underline-offset-2 group-hover:underline'
      }
    }
  },
  compoundVariants: [
    {
      variant: 'info',
      gradient: true,
      class: {
        root: 'bg-[linear-gradient(90deg,#e9f9fb_44.789%,#bbffc6_165.06%)]'
      }
    },
    {
      variant: 'success',
      gradient: true,
      class: {
        root: 'bg-[linear-gradient(103.005deg,#e7fae8_20.073%,#94b396_265.57%)]'
      }
    },
    {
      variant: 'warning',
      gradient: true,
      class: {
        root: 'bg-[linear-gradient(102.934deg,#fff2db_44.984%,#ff8c5b_310.24%)]'
      }
    },
    {
      variant: 'error',
      gradient: true,
      class: {
        root: 'bg-[linear-gradient(103.696deg,#fdedee_66.117%,#c7638e_238.19%)]'
      }
    }
  ],
  defaultVariants: {
    variant: 'info',
    gradient: false,
    interactive: false
  }
})

export type MarketingBannerVariant = NonNullable<
  VariantProps<typeof marketingBannerVariants>['variant']
>

export type MarketingBannerProps = React.ComponentPropsWithRef<'div'> &
  Omit<VariantProps<typeof marketingBannerVariants>, 'interactive'> & {
    /** Renders the message + arrow as an anchor pointing to this URL. */
    href?: string
    /** Renders the message + arrow as a button when no `href` is given. */
    onLinkClick?: React.MouseEventHandler<HTMLElement>
    showArrow?: boolean
    /** Shows the close button when provided. */
    onDismiss?: () => void
    dismissLabel?: string
  }

export const MarketingBanner = forwardRef<HTMLDivElement, MarketingBannerProps>(
  (
    {
      variant,
      gradient,
      href,
      onLinkClick,
      showArrow = true,
      onDismiss,
      dismissLabel = 'Dismiss',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const resolvedVariant: MarketingBannerVariant = variant ?? 'info'
    const hasAction = Boolean(href || onLinkClick)
    const { root, content, text, dismiss } = marketingBannerVariants({
      variant: resolvedVariant,
      gradient,
      interactive: hasAction
    })

    const message = (
      <>
        <span data-slot='marketing-banner-text' className={text()}>
          {children}
        </span>
        {hasAction && showArrow && (
          <Icon name='arrow_right_alt' aria-hidden='true' />
        )}
      </>
    )

    let Tag: React.ElementType = 'span'
    let actionProps: Record<string, unknown> = {}
    if (href) {
      Tag = 'a'
      actionProps = { href, onClick: onLinkClick }
    } else if (onLinkClick) {
      Tag = 'button'
      actionProps = { type: 'button', onClick: onLinkClick }
    }

    return (
      <div
        ref={ref}
        data-slot='marketing-banner'
        data-variant={resolvedVariant}
        data-gradient={gradient ? '' : undefined}
        className={cn(root(), className)}
        {...props}
      >
        <Tag
          data-slot={
            hasAction ? 'marketing-banner-link' : 'marketing-banner-content'
          }
          className={content()}
          {...actionProps}
        >
          {message}
        </Tag>
        {onDismiss && (
          <button
            type='button'
            aria-label={dismissLabel}
            onClick={onDismiss}
            data-slot='marketing-banner-dismiss'
            className={dismiss()}
          >
            <Icon name='close' aria-hidden='true' />
          </button>
        )}
      </div>
    )
  }
)
MarketingBanner.displayName = 'MarketingBanner'
